import { sceneSlice } from "./sceneSlice";
import { selectUsers } from "./selectors";
import { sessionSlice } from "./sessionSlice";
import {
  createUser,
  updateUserVisibility,
  renderUserUpdate,
  removeUserFromScene,
  addElement,
  updateElementScale,
  deleteElement,
  recenterElement,
  cleanUp,
  updateElementTransform,
  updateSlide,
  startVRSession,
  startARSession,
  endVRSession,
  enableWasd,
  disableWasd,
  updatePositionFromJoystick,
  joystickUpdateEnd,
  renderPdb,
  renderAtomsUpdate,
  updateAtomColors,
  updateStickRadius,
  updateCameraControls,
  setup3DScene,
  addRubber3D,
  removeRubber3D,
  removeAllRubbers,
  clearAtoms,
  display3DToast,
  updateGimbalTransform,
  updateGimbalOwnership,
  setGimbalRotationByValue,
  changeGimbalRotation,
} from "../3d/index";
import {
  checkTouchScreen,
  saveSessionCookie,
  checkSessionCookie,
  checkAdminCookie,
  clearSessionCookie,
  downloadPDBfile,
} from "./utils";
import { socket } from "./socket";
import { speakup } from "../ui/components/LlmModule/utils";
import { measureConnectionQuality } from "./connectionQuality";

// trajectory recording state
const pendingWritePDBQueue = []; // 'manual' | 'trajectory' — TCP order guarantees correct pairing
let trajectoryFrames = [];
let trajectoryModelNo = 0;

// compression / decompression for coordinate updates
import { deflate, inflate } from "pako";
import { decode, encode } from "@msgpack/msgpack";

function pack(data) {
  // serialize and compress
  const bdata = encode(data);
  return deflate(bdata);
}

function unpack(zraw) {
  // decompress and deserialize
  const raw = inflate(zraw);
  return decode(raw);
}

const socketMiddleware = (store) => {
  // Register all socket listeners once at middleware init.
  // socket.connect() is called later when startConnecting is dispatched.

  socket.on("connect", async () => {
    // 1. Run quality test before marking the connection as established
    store.dispatch(sessionSlice.actions.qualityTestStarted());
    const quality = await measureConnectionQuality(socket);

    const urlParams2 = new URLSearchParams(window.location.search);
    const badwifiAllowed = urlParams2.get('badwifi') === 'true'; //|| true; // bypass

    if (badwifiAllowed) {
      alert("Allowing you to enter even when WiFi check fails. Check console logs to know what we've measured.")
    }

    if (!quality.passed && !badwifiAllowed) {
      store.dispatch(sessionSlice.actions.qualityTestFailed(quality));
      socket.disconnect();
      return;
    }

    store.dispatch(sessionSlice.actions.qualityTestPassed(quality));

    // 2. Detect device capabilities
    store.dispatch(sessionSlice.actions.connectionStablished({ id: socket.id }));

    if ("xr" in navigator) {
      navigator.xr.isSessionSupported("immersive-vr").then(function (supported) {
        if (supported) {
          store.dispatch(sessionSlice.actions.isVrUser());
        }
      });

      navigator.xr.isSessionSupported("immersive-ar").then(function (supported) {
        if (supported) {
          store.dispatch(sessionSlice.actions.isArUser());
        }
      });
    }

    const isTouch = checkTouchScreen();
    if (isTouch) {
      store.dispatch(sessionSlice.actions.isTouchScreen());
    }

    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const sessionId = urlParams.get("roomId") || checkSessionCookie();

    if (sessionId) {
      const userType = checkAdminCookie();
      socket.emit("request:joinRoom", { roomName: sessionId, userType }, (response) => {
        console.log(response);
      });
    }
  });

  socket.on("disconnect", () => {
    store.dispatch(sessionSlice.actions.connectionLost());
    endVRSession();
    cleanUp();
  });

  socket.on("scenes:get", (data) => {
    store.dispatch(sessionSlice.actions.receivedAvailableRooms(data));
  });

  socket.on("users:get", async (data) => {
    const state = store.getState();
    store.dispatch(sceneSlice.actions.receivedStartingPackage(data));
    store.dispatch(sessionSlice.actions.receivedStartingPackage(data));
    store.dispatch(sceneSlice.actions.setupScene());

    if (data.type === "ELEMENTS") {
      data.elements.forEach((element) => {
        addElement({ element });
      });
    }

    if (data.type === "ATOMS") {
      renderPdb(data.pdb, data.atomData);
      data.rubbers.forEach((rubber) => addRubber3D(rubber));

      // Initialize gimbal state from server
      if (data.gimbal) {
        updateGimbalOwnership({ owner: data.gimbal.owner });
        updateGimbalTransform({
          position: data.gimbal.position,
          quaternion: data.gimbal.quaternion,
        });
      }
    }

    data.users.forEach((user) => {
      createUser(user);
    });

    updateSlide({ url: data.slideUrl });

    if (data.admin) {
      store.dispatch(sessionSlice.actions.setAdmin());
    }

    enableWasd();
    let userType;
    if (data.admin) {
      userType = "admin";
    } else if (data.isAttendee) {
      userType = "attendee";
    } else {
      userType = "guest";
    }
    saveSessionCookie(data.roomId, userType);

    if (state.session.isVr) {
      socket.emit("request:spawnPoint", { id: socket.id }, (response) => {
        store.dispatch(sceneSlice.actions.receivedSpawnPoint(response.spawnPoint));
      });
    }
  });

  socket.on("controller:renderUpdate", (data) => {
    if (data.id !== socket.id) {
      renderUserUpdate(data);
    }
  });

  socket.on("user:add", async (data) => {
    const state = store.getState();
    const users = selectUsers(state);

    const userIndex = users.findIndex((user) => user.id === data.id);
    const userExist = userIndex >= 0;

    if (!userExist) {
      store.dispatch(sceneSlice.actions.userAdded(data));
    }

    if (data.id !== socket.id) {
      console.log(`Connect ${data.id} ${data.type}`);
      if (userExist) {
        store.dispatch(
          sceneSlice.actions.userUpdatedVisibility({
            id: data.id,
            visibility: true,
          })
        );
        updateUserVisibility(data.id, true);
      } else {
        createUser(data);
        const message = `${data.name} joined the session`;
        store.dispatch(sessionSlice.actions.displayToast({ message }));
        display3DToast(message);
      }
    }
  });

  socket.on("user:hide", (data) => {
    if (data.id !== socket.id) {
      const state = store.getState();
      const users = selectUsers(state);

      const userIndex = users.findIndex((user) => user.id === data.id);

      if (userIndex >= 0) {
        store.dispatch(
          sceneSlice.actions.userUpdatedVisibility({
            id: data.id,
            visibility: false,
          })
        );
        updateUserVisibility(data.id, false);
      }
    }
  });

  socket.on("user:remove", (data) => {
    if (data.id !== socket.id) {
      store.dispatch(sceneSlice.actions.userRemoved({ id: data.id }));
      removeUserFromScene(data.id);
    }
  });

  socket.on("user:updateGrab", (data) => {
    store.dispatch(sceneSlice.actions.permissionUpdated({ canGrab: data.canGrab }));
  });

  socket.on("scene:updateCanGrabMultipleObjs", (data) => {
    store.dispatch(
      sceneSlice.actions.canGrabMultipleObjsUpdated({
        canGrabMultipleObjs: data.canGrabMultipleObjs,
      })
    );
  });

  socket.on("element:add", async (data) => {
    store.dispatch(sceneSlice.actions.elementAdded(data));
    addElement(data);
  });

  socket.on("element:updateScale", async (data) => {
    store.dispatch(sceneSlice.actions.elementScaleUpdated(data));
    updateElementScale(data);
  });

  socket.on("atoms:updateScale", async (data) => {
    store.dispatch(sceneSlice.actions.atomsScaleUpdated(data));
    store.dispatch(sessionSlice.actions.displayToast({ message: "Simulation scale has been updated" }));
    //display3DToast("Scale updated");  //commented out because it's annoying***
  });

  socket.on("atoms:reRender", async (data) => {
    clearAtoms();
    renderPdb(data.pdb, data.atomData);
  });

  socket.on("element:updateEnable", async (data) => {
    store.dispatch(sceneSlice.actions.elementEnableUpdated(data));
    // updateElementScale(data);
  });

  socket.on("element:remove", async (data) => {
    store.dispatch(sceneSlice.actions.elementDeleted(data));
    deleteElement(data);
  });

  socket.on("element:recenter", async (data) => {
    recenterElement(data);
    store.dispatch(sceneSlice.actions.elementClearGrabbing(data));
  });

  socket.on("element:updateGrab", async (data) => {
    store.dispatch(sceneSlice.actions.elementGrabbingUpdated(data));
  });

  socket.on("element:renderUpdate", (data) => {
    if (data.ownerId !== socket.id) {
      updateElementTransform(data);
    }
  });

  socket.on("scene:resetEls", (data) => {
    const state = store.getState();
    state.scene.elements.forEach((el) => {
      deleteElement({ id: el.id });
    });

    data.elements.forEach((element) => {
      addElement({ element });
    });

    store.dispatch(sceneSlice.actions.updateElementList(data));
  });

  socket.on("slide:update", async (data) => {
    updateSlide(data);
  });

  socket.on("session:toggleAudio", async (data) => {
    store.dispatch(sessionSlice.actions.toggleAudioChannel(data));
  });

  socket.on("session:toggleMute", async (data) => {
    store.dispatch(sessionSlice.actions.toggleMute(data));
  });

  socket.on("session:toggleMuteGuests", async (data) => {
    store.dispatch(sessionSlice.actions.toggleMuteGuests(data));
  });

  socket.on("atoms:update", async (zdata) => {
    let data = unpack(zdata);
    renderAtomsUpdate(data.atomData);
  });

  socket.on("atoms:updateColors", async (zdata) => {
    let data = unpack(zdata);
    updateAtomColors(data.colors);
    store.dispatch(sessionSlice.actions.displayToast({ message: "Colors updated." }));
  });

  socket.on("atoms:updateStickRadius", async (zdata) => {
    let data = unpack(zdata);
    updateStickRadius(data);
    store.dispatch(sessionSlice.actions.displayToast({ message: "Visualization updated." }));
  });

  socket.on("atoms:writepdb", async (data) => {
    const requestType = pendingWritePDBQueue.shift() ?? "manual";

    if (requestType === "trajectory") {
      trajectoryModelNo++;
      const frame =
        `MODEL     ${String(trajectoryModelNo).padStart(4, " ")}\n` +
        data.replace(/END\n$/, "ENDMDL\n");
      trajectoryFrames.push(frame);
    } else {
      downloadPDBfile("iMD_frame.pdb", data);
      store.dispatch(
        sessionSlice.actions.displayToast({
          message:
            "PDB file written. It should show up in your downloads.\n\nIf this file looks very different to your input file, consider that this might be because certain atoms are added or removed during simulation setup. For example, for AMBER simulations all H atoms are added; and for CALVADOS the atoms are condensed into CG beads.",
        })
      );
    }
  });

  socket.on("atoms:minimizing", async () => {
    store.dispatch(sessionSlice.actions.minimizing());
  });

  socket.on("atoms:stopMinimizing", async () => {
    store.dispatch(sessionSlice.actions.stopMinimizing());
  });

  socket.on("atoms:recoveredTenTimes", () => {
    switch (Math.floor(Math.random() * 4)) {
      case 0:
        store.dispatch(
          sessionSlice.actions.displayToast({
            message: "Simulation had to be recovered. Crashes decrease performance.",
          })
        );
        break;
      case 1:
        store.dispatch(sessionSlice.actions.displayToast({ message: "Simulation had to be recovered." }));
        break;
      case 2:
        store.dispatch(
          sessionSlice.actions.displayToast({ message: "Simulation had to be recovered. Move atoms more gently." })
        );
        break;
      default:
        store.dispatch(
          sessionSlice.actions.displayToast({
            message: "Pulling atoms too hard makes the system crash. Simulation had to be recovered.",
          })
        );
    }

    console.log("Simulation recovered for 5 consecutive times.");
    speakup("Move atoms gently to avoid crashes!");
    store.dispatch(sceneSlice.actions.removeAllRubbers());
    removeAllRubbers();
  });

  socket.on("atoms:recoveredSimulation", () => {
    display3DToast("Move atoms slowly!!");

    store.dispatch(sceneSlice.actions.removeAllRubbers());
    removeAllRubbers();
  });

  socket.on("atoms:simUpdate", async (data) => {
    store.dispatch(sessionSlice.actions.simUpdated(data));
  });

  socket.on("atoms:addRubber", (data) => {
    store.dispatch(sceneSlice.actions.addedRubber(data));
    addRubber3D(data.rubber);
  });

  socket.on("atoms:removeRubber", (data) => {
    store.dispatch(sceneSlice.actions.removedRubber(data));
    removeRubber3D(data.id);
  });

  socket.on("room:close", (data) => {
    store.dispatch(sessionSlice.actions.sessionClosed(data));
    cleanUp();
    disableWasd();
    endVRSession();
  });

  socket.on("atoms:updateTemp", (data) => {
    store.dispatch(sceneSlice.actions.updatedTemp(data));
  });

  socket.on("atoms:updateRates", (data) => {
    store.dispatch(sceneSlice.actions.updatedRates(data));
  });

  // Gimbal socket events
  socket.on("gimbal:updateGrab", (data) => {
    store.dispatch(sceneSlice.actions.gimbalGrabbingUpdated(data));
    updateGimbalOwnership(data);
  });

  socket.on("gimbal:renderUpdate", (data) => {
    if (data.ownerId !== socket.id) {
      updateGimbalTransform(data);
    }
  });

  return (next) => (action) => {
    if (sessionSlice.actions.startConnecting.match(action)) {
      socket.connect();
    }

    if (sceneSlice.actions.emitControllerConnect.match(action)) {
      socket.emit("controller:connect", action.payload);
    }

    if (sceneSlice.actions.emitControllerDisconnect.match(action)) {
      socket.emit("controller:disconnect");
    }

    if (sceneSlice.actions.emitControllerUpdate.match(action)) {
      socket.volatile.emit("controller:update", action.payload);
    }

    if (sceneSlice.actions.emitElementSubmit.match(action)) {
      socket.emit("element:submit", action.payload);
    }

    if (sceneSlice.actions.emitScaleUpdate.match(action)) {
      socket.emit("element:scale", action.payload);
    }

    if (sceneSlice.actions.emitMoleculeScaleUpdate.match(action)) {
      socket.emit("atoms:scale", action.payload);
    }

    if (sceneSlice.actions.emitMoleculeScaleSet.match(action)) {
      socket.emit("atoms:setScale", action.payload);
    }

    if (sceneSlice.actions.emitUpdateRatesChange.match(action)) {
      socket.emit("atoms:updateRatesChange", action.payload);
    }

    if (sceneSlice.actions.emitTemperatureChange.match(action)) {
      socket.emit("atoms:temperatureChange", action.payload);
    }

    if (sceneSlice.actions.emitTemperatureSet.match(action)) {
      socket.emit("atoms:temperatureSet", action.payload);
    }

    if (sceneSlice.actions.emitSetAtomRadiusFactors.match(action)) {
      socket.emit("atoms:setAtomsRadiusFactor", action.payload);
    }

    if (sceneSlice.actions.emitSetAtomColors.match(action)) {
      socket.emit("atoms:setAtomColors", action.payload);
    }

    if (sceneSlice.actions.emitSetStickRadius.match(action)) {
      socket.emit("atoms:setStickRadius", action.payload);
    }

    if (sceneSlice.actions.emitSetSelection.match(action)) {
      socket.emit("atoms:setSelection", action.payload);
    }

    if (sceneSlice.actions.emitElementDelete.match(action)) {
      socket.emit("element:delete", action.payload);
    }

    if (sceneSlice.actions.emitElementRecenter.match(action)) {
      socket.emit("element:center", action.payload);
    }

    if (sceneSlice.actions.emitElementEnable.match(action)) {
      socket.emit("element:toggleEnable", action.payload);
    }

    if (sceneSlice.actions.emitElementGrabbed.match(action)) {
      socket.emit("element:grab", { ...action.payload, owner: socket.id });
    }

    if (sceneSlice.actions.emitElementUngrabbed.match(action)) {
      socket.emit("element:ungrab", action.payload);
    }

    if (sceneSlice.actions.emitElementUpdate.match(action)) {
      socket.emit("element:updatePos", action.payload);
    }

    if (sceneSlice.actions.emitUserPermissionUpdate.match(action)) {
      socket.emit("user:grabPermission", action.payload);
    }

    if (sceneSlice.actions.emitGrabbingMultipleObjs.match(action)) {
      socket.emit("session:canGrabMultipleObjs", action.payload);
    }

    if (sceneSlice.actions.emitSlideUpdate.match(action)) {
      socket.emit("slide:submit", action.payload);
    }

    if (sessionSlice.actions.emitToggleAudioChannel.match(action)) {
      socket.emit("session:toggleAudio");
    }

    if (sessionSlice.actions.emitToggleMute.match(action)) {
      socket.emit("session:toggleMute");
    }

    if (sessionSlice.actions.emitToggleMuteGuests.match(action)) {
      socket.emit("session:toggleMuteGuests");
    }

    if (sessionSlice.actions.emitRequestNewRoom.match(action)) {
      let data = pack(action.payload);
      socket.emit("request:newRoom", data);
    }

    if (sessionSlice.actions.emitRequestJoinRoom.match(action)) {
      socket.emit("request:joinRoom", action.payload, (response) => {
        store.dispatch(sessionSlice.actions.noRoomFound());
      });
    }

    if (sceneSlice.actions.enterVR.match(action)) {
      startVRSession();
    }

    if (sceneSlice.actions.enterAR.match(action)) {
      startARSession();
    }

    if (sceneSlice.actions.setupScene.match(action)) {
      setup3DScene();
    }

    if (sessionSlice.actions.emitResetScene.match(action)) {
      socket.emit("scene:reset");
    }

    if (sceneSlice.actions.joystickUpdate.match(action)) {
      updatePositionFromJoystick(action.payload);
    }

    if (sceneSlice.actions.joystickEnd.match(action)) {
      joystickUpdateEnd();
    }

    if (sceneSlice.actions.changedCameraControls.match(action)) {
      updateCameraControls(action.payload);
    }

    if (sessionSlice.actions.emitCloseSession.match(action)) {
      socket.emit("scene:close");
    }

    if (sessionSlice.actions.emitLeaveSession.match(action)) {
      clearSessionCookie();
      cleanUp();
      socket.emit("scene:leave");
    }

    if (sessionSlice.actions.requestTrial.match(action)) {
      const state = store.getState();
      enableWasd();
      state.session.trialScene.elements.forEach((element) => {
        addElement({ element });
      });

      updateSlide({ url: state.session.trialSlide });
    }

    if (sessionSlice.actions.leaveTrial.match(action)) {
      disableWasd();
      cleanUp();
    }

    if (sceneSlice.actions.emitRemotePinchStart.match(action)) {
      socket.emit("user:pinchStart", action.payload);
    }

    if (sceneSlice.actions.emitRemotePinchEnd.match(action)) {
      socket.emit("user:pinchEnd", action.payload);
    }

    // Gimbal emit actions
    if (sceneSlice.actions.emitGimbalGrabbed.match(action)) {
      socket.emit("gimbal:grab", { owner: socket.id });
    }

    if (sceneSlice.actions.emitGimbalUngrabbed.match(action)) {
      socket.emit("gimbal:ungrab");
    }

    if (sceneSlice.actions.emitGimbalUpdate.match(action)) {
      socket.volatile.emit("gimbal:update", action.payload);
    }

    if (sessionSlice.actions.emitToggleSim.match(action)) {
      socket.emit("atoms:toggleSim", action.payload);
    }

    if (sessionSlice.actions.emitWritePDB.match(action)) {
      pendingWritePDBQueue.push("manual");
      socket.emit("atoms:writePDB", action.payload);
    }

    if (sessionSlice.actions.emitTrajectoryFrame.match(action)) {
      pendingWritePDBQueue.push("trajectory");
      socket.emit("atoms:writePDB", action.payload);
    }

    if (sessionSlice.actions.startTrajectoryLogging.match(action)) {
      trajectoryFrames = [];
      trajectoryModelNo = 0;
    }

    if (sessionSlice.actions.stopTrajectoryLogging.match(action)) {
      if (trajectoryFrames.length > 0) {
        const trajectoryString = trajectoryFrames.join("") + "END\n";
        downloadPDBfile("iMD_trajectory.pdb", trajectoryString);
        store.dispatch(
          sessionSlice.actions.displayToast({
            message: `Trajectory downloaded: ${trajectoryFrames.length} frames.`,
          })
        );
      }
      trajectoryFrames = [];
      trajectoryModelNo = 0;
    }

    if (sessionSlice.actions.emitStartSim.match(action)) {
      socket.emit("atoms:startSim", action.payload);
    }

    if (sessionSlice.actions.emitStopSim.match(action)) {
      socket.emit("atoms:stopSim", action.payload);
    }

    if (sessionSlice.actions.emitSetMolRotationByValue.match(action)) {
      console.log("Setting rotation to new value:")
      console.log(action.payload)
      socket.emit("atoms:setMolRotationByValue", action.payload);
      setGimbalRotationByValue(action.payload.newMolRotations);
    }

    if (sessionSlice.actions.emitSetMolRotationChange.match(action)) {
      console.log("Changing rotation by:")
      console.log(action.payload)
      socket.emit("atoms:setMolRotationChange", action.payload);
      changeGimbalRotation(action.payload.newMolRotations);
    }

    next(action);
  };
};

export default socketMiddleware;
